import "./DealStagesSection.css";

import { DragEvent } from "react";

import { HamburgerMenuGreyIcon, RoundedPlusSignIcon, SortArrowsDarkIcon, TrashDark } from "../../../../assets";
import { Button, KanaTooltip, TextInput } from "../../../../widget";
import { DealWorkflowStage } from "../../models";

const KEY_DATES_TOOLTIP_TEXT =
  "Key dates are events that tasks can be tracked against in the workflow. These dates should occur with each instance of the workflow. Start and end date are default key dates that cannot be removed";

interface DealStagesSectionProps {
  canAddStage: boolean;
  canRemoveStage: boolean;
  canReorderStages: boolean;
  dealStages: DealWorkflowStage[];
  hideDefaultKeyDates?: boolean;
  isReorderMode: boolean;
  onAddKeyDate: (stageId: string) => void;
  onAddStage: () => void;
  onCancelReorder: () => void;
  onEnterReorderMode: () => void;
  onRemoveKeyDate: (stageId: string, keyDateId: string) => void;
  onRemoveStage: (stageId: string) => void;
  onReorderStageDrop: (droppedOnStageId: string, draggedStageId: string) => void;
  onSaveReorder: () => void;
  onUpdateKeyDate: (stageId: string, keyDateId: string, value: string) => void;
  onUpdateStageDescription: (stageId: string, value: string) => void;
  onUpdateStageName: (stageId: string, value: string) => void;
}

export const DealStagesSection = ({
  canAddStage,
  canRemoveStage,
  canReorderStages,
  dealStages,
  hideDefaultKeyDates = false,
  isReorderMode,
  onAddKeyDate,
  onAddStage,
  onCancelReorder,
  onEnterReorderMode,
  onRemoveKeyDate,
  onRemoveStage,
  onReorderStageDrop,
  onSaveReorder,
  onUpdateKeyDate,
  onUpdateStageDescription,
  onUpdateStageName,
}: DealStagesSectionProps): JSX.Element => {
  const handleStageDragStart = (event: DragEvent<HTMLDivElement>, stageId: string): void => {
    event.dataTransfer.setData("stageId", stageId);
  };

  const handleStageDrop = (event: DragEvent<HTMLDivElement>, stageId: string): void => {
    const draggedStageId = event.dataTransfer.getData("stageId");
    onReorderStageDrop(stageId, draggedStageId);
  };

  return (
    <div className="DealStagesSection">
      <div className="DealStagesSectionHeaderRow">
        <div className="DealStagesSectionHeader">
          <h2>Stages</h2>
          <p className="body2">Define up to 10 stages for this deal workflow.</p>
        </div>
        {!isReorderMode && dealStages.length > 1 && (
          <Button
            className="DealStagesReorderButton"
            text="Re-order stages"
            icon={<SortArrowsDarkIcon />}
            iconPosition="right"
            variant="quaternary"
            textUnderline
            hasNoPadding
            minWidth={false}
            size="small"
            disabled={!canReorderStages}
            onClick={onEnterReorderMode}
          />
        )}
      </div>
      <div className="DealStagesSectionList">
        {isReorderMode
          ? dealStages.map((stage) => {
              return (
                <div
                  key={stage.id}
                  className="DealStageReorderCard"
                  draggable
                  onDragStart={(event) => handleStageDragStart(event, stage.id)}
                  onDrop={(event) => handleStageDrop(event, stage.id)}
                  onDragOver={(event) => event.preventDefault()}
                >
                  <HamburgerMenuGreyIcon className="DealStageReorderHandle" />
                  <p className="DealStageReorderName">{stage.name || "Untitled stage"}</p>
                </div>
              );
            })
          : dealStages.map((stage) => {
              return (
                <div key={stage.id} className="DealStageCard">
                  <div className="DealStageCardContent">
                    <div className="DealStageCardFields">
                      <TextInput
                        label="Stage name"
                        name={`stage-name-${stage.id}`}
                        value={stage.name}
                        onChange={(value) => onUpdateStageName(stage.id, value)}
                        maxLength={100}
                      />
                      <TextInput
                        label="Stage description"
                        name={`stage-description-${stage.id}`}
                        value={stage.description}
                        onChange={(value) => onUpdateStageDescription(stage.id, value)}
                        maxLength={500}
                      />
                    </div>
                    <div className="DealStageKeyDates">
                      {stage.keyDates.map((keyDate, index) => {
                        if (hideDefaultKeyDates && index < 2) {
                          return null;
                        }

                        const isDefaultKeyDate = index < 2;

                        return (
                          <div key={keyDate.id} className="DealStageKeyDateRow">
                            <div className="DealStageKeyDateInput">
                              <TextInput
                                label="Key date name"
                                name={`key-date-${stage.id}-${keyDate.id}`}
                                value={keyDate.name}
                                disabled={isDefaultKeyDate}
                                onChange={(value) => onUpdateKeyDate(stage.id, keyDate.id, value)}
                                maxLength={100}
                              />
                            </div>
                            {!isDefaultKeyDate && (
                              <div className="DealStageKeyDateRemove">
                                <Button
                                  text="Remove"
                                  variant="reversedPrimary"
                                  textUnderline
                                  hasNoPadding
                                  size="small"
                                  onClick={() => onRemoveKeyDate(stage.id, keyDate.id)}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="DealStageAddKeyDate">
                        <Button
                          className="DealStageAddKeyDateButton"
                          text="Add key date"
                          icon={<RoundedPlusSignIcon />}
                          iconPosition="left"
                          variant="reversedPrimary"
                          textUnderline
                          hasNoPadding
                          minWidth={false}
                          size="small"
                          onClick={() => onAddKeyDate(stage.id)}
                        />
                        <KanaTooltip tooltipHeader="Key dates" tooltipText={KEY_DATES_TOOLTIP_TEXT} />
                      </div>
                    </div>
                  </div>
                  <div className="DealStageCardActions">
                    <Button
                      className="DealStageDeleteButton"
                      text=""
                      icon={<TrashDark />}
                      variant="quaternary"
                      size="small"
                      hasNoPadding
                      minWidth={false}
                      disabled={!canRemoveStage}
                      onClick={() => onRemoveStage(stage.id)}
                    />
                  </div>
                </div>
              );
            })}
      </div>
      {isReorderMode ? (
        <div className="DealStageReorderActions">
          <Button text="Save" size="small" minWidth={false} onClick={onSaveReorder} />
          <Button
            text="Cancel"
            variant="reversedPrimary"
            size="small"
            hasNoPadding
            minWidth={false}
            onClick={onCancelReorder}
          />
        </div>
      ) : (
        <div className="DealStageAddStage">
          <Button text="Add stage" variant="tertiary" disabled={!canAddStage} onClick={onAddStage} />
        </div>
      )}
    </div>
  );
};
